import React, {createContext, useContext} from 'react';
import {useDispatch} from 'react-redux';
import {Form, FormikProvider, useFormik} from 'formik';
import {useNavigate} from 'react-router-dom';
import {SHA256} from 'crypto-js';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useAuthContext} from '../../../../common/components/core/AuthContext';
import {setToken, setUser} from '../../redux/actions/authActions';
import {AppDispatch} from '../../../../store/configureStore';
import {
  actionLogMessages as M,
  actionResults as R,
  actionSubjects as S,
  actionTypes as T,
  actionPages as AP,
  pathnames as P
} from '../../../../common/enums';
import {getErrorMessage} from '../../../../common/utils';

export type LoginContext = {
  onClick: () => Promise<void>
};

export type LoginContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: LoginContext = {
  onClick: async () => {}
};

export type LoginFormValues = {
  username: string,
  password: string,
}

export const defaultFormValues: LoginFormValues = {
  username: '',
  password: '',
};

export const LoginContext = createContext<LoginContext>(defaultContext);
LoginContext.displayName = 'LoginContext';

export const useLoginContext = () => useContext(LoginContext);

export const LoginContextProvider = ({children}: LoginContextProviderProps) => {
  const {apiService} = useGlobalContext();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const {handleLogin} = useAuthContext();

  const formik = useFormik<LoginFormValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof LoginFormValues]: string }> = {};
      if (!values.username) errors.username = E.requiredField;
      if (!values.password) errors.password = E.requiredField;

      return errors;
    },
    onSubmit: () => {},
    enableReinitialize: true,
  });

  const onClick = async () => {
    const {values} = formik;
    try {
      const encryptedPassword = SHA256(values.password);
      const result = await apiService.login(values.username, encryptedPassword.toString());
      const token = result.body.token;
      const user = result.body.user;

      if (result.errorCode < 0) {
        globalMessage.open(getErrorMessage(result.errorCode, E));
      } else {
        if (token) {
          dispatch(setToken(token));
          apiService.setToken(token);
        }
        if (user) {
          dispatch(setUser(user));
        }
        await handleLogin();
        navigate(P.HOME);

        // if (token && user) {
        //   await apiService.logUserAction(
        //     AP.AUTH,
        //     S.LOGIN,
        //     T.GENERAL,
        //     M.LOGIN(values.username),
        //     result.errorCode === 0 ? R.SUCCESS : R.FAIL
        //   )
        // }
      }
    } catch (e) {
      globalMessage.open(E.serverError);
      dispatch(setToken(''));
      dispatch(setUser(null));
    }
  };

  const context: LoginContext = {
    ...defaultContext,
    onClick
  };

  return (
    <LoginContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </LoginContext.Provider>
  );
};
